import { toWordsUSD, toWords } from '@/utils/transactionbill/toWordsConfig';
import { IAccountBillContentBlock } from './type';

interface Props {
  blockProps: IAccountBillContentBlock;
  scaleFactor?: number;
  maxWidth: number;
  priceShow?: number;
  loopLength?: number;
  config?: any;
}

export const VoucherBill: React.FC<Props> = (props) => {
  const { blockProps, scaleFactor = 1, maxWidth, config } = props;
  const { data, type, currencyType } = blockProps;
  const columns = data?.column_name || [];
  const minHeight =
    config?.height - config?.header?.height - config?.footer?.height;

  const tableHeaderBgColor = data?.tableHeaderBgColor ?? 'none';
  const borderColor = data?.borderColor ?? '#000000';

  const voucherData: any[] = data?.voucherEntries || [];

  const rawTotals = voucherData.reduce(
    (acc, entry) => {
      const debit = Number(entry.debitAmount ?? 0);
      const credit = Number(entry.creditAmount ?? 0);

      acc.debitAmount += isNaN(debit) ? 0 : debit;
      acc.creditAmount += isNaN(credit) ? 0 : credit;
      return acc;
    },
    { debitAmount: 0, creditAmount: 0 },
  );

  const totals = {
    debitAmount: rawTotals.debitAmount,
    creditAmount: rawTotals.creditAmount,
  };

  return (
    <>
      <div
        style={{
          width: maxWidth - Number(data?.tablePaddingRight || 0),
          fontSize: 12 * scaleFactor,
          minHeight: minHeight - 25,
          fontFamily: config?.default_font_family || 'Arial',
          display: 'flex',
          flexDirection: 'column',
          borderCollapse: 'collapse',
        }}
      >
        <div
          style={{
            fontWeight: 500,
            fontSize: 14,
            padding: '5px 0',
            textAlign: 'start',
          }}
        >
          {type === 'payment' ? 'Paid To' : 'Received From'}
        </div>
        <div
          style={{
            display: 'table',
            width: '100%',
            borderCollapse: 'collapse',
          }}
        >
          {(type === 'receipt' || type === 'payment') && (
            <div>
              <div
                style={{
                  display: 'table',
                  width: '100%',
                  borderCollapse: 'collapse',
                }}
              >
                <div
                  style={{
                    display: 'table-row',
                    backgroundColor: tableHeaderBgColor,
                    borderTop: `1px solid ${borderColor}`,
                  }}
                >
                  {[
                    {
                      key: 'Account Name',
                      width: 50,
                      dataIndex: 'ledgerName',
                    },
                    { key: 'Amount [NPR]', width: 20, dataIndex: 'credit' },
                    {
                      key: 'Short Narration',
                      width: 30,
                      dataIndex: 'remarks',
                    },
                  ]?.map((column) => (
                    <div
                      key={column.key}
                      style={{
                        display: 'table-cell',
                        width: `${Number(column?.width || 0)}%`,
                        padding: '7px',
                        borderRight: `1px solid ${borderColor}`,
                        borderBottom: `1px solid ${borderColor}`,
                        borderLeft: `1px solid ${borderColor}`,
                        boxSizing: 'border-box',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        fontWeight: 700,
                        textAlign: [
                          'S.N.',
                          'Account Name',
                          'Short Narration',
                        ].includes(column?.key)
                          ? 'left'
                          : 'right',
                      }}
                    >
                      {column?.key}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {(type === 'receipt' || type === 'payment') && (
            <>
              <div
                style={{
                  display: 'table',
                  width: '100%',
                  borderCollapse: 'collapse',
                }}
              >
                {voucherData
                  .filter((item) =>
                    type === 'payment'
                      ? Number(item?.debitAmount || 0) > 0
                      : Number(item?.creditAmount || 0) > 0,
                  )
                  .map((row, rowIndex) => (
                    <div key={rowIndex} style={{ display: 'table-row' }}>
                      {[
                        {
                          key: 'Account Name',
                          width: 50,
                          dataIndex: 'ledgerName',
                        },
                        {
                          key: 'Amount [NPR]',
                          width: 20,
                          dataIndex:
                            type === 'payment' ? 'debitAmount' : 'creditAmount',
                        },
                        {
                          key: 'Short Narration',
                          width: 30,
                          dataIndex: 'remarks',
                        },
                      ].map((column) => (
                        <div
                          key={column.key}
                          style={{
                            display: 'table-cell',
                            width: `${column.width}%`,
                            padding: '7px',
                            borderRight: `1px solid ${borderColor}`,
                            borderBottom: `1px solid ${borderColor}`,
                            borderLeft: `1px solid ${borderColor}`,
                            whiteSpace: 'wrap',
                            textAlign: [
                              'S.N.',
                              'Account Name',
                              'Short Narration',
                            ].includes(column.key)
                              ? 'left'
                              : 'right',
                          }}
                        >
                          {row?.[column?.dataIndex]}
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>

        <div
          style={{
            fontWeight: 500,
            fontSize: 14,
            padding: '5px 0',
            textAlign: 'start',
          }}
        >
          {type === 'payment' ? 'Paid From' : 'Received Into'}
        </div>
        <div
          style={{
            display: 'table',
            width: '100%',
            borderCollapse: 'collapse',
          }}
        >
          {(type === 'receipt' || type === 'payment') && (
            <div>
              <div
                style={{
                  display: 'table',
                  width: '100%',
                  borderCollapse: 'collapse',
                }}
              >
                <div
                  style={{
                    display: 'table-row',
                    backgroundColor: tableHeaderBgColor,
                    borderTop: `1px solid ${borderColor}`,
                  }}
                >
                  {[
                    {
                      key: 'Account Name',
                      width: 50,
                      dataIndex: 'ledgerName',
                    },
                    { key: 'Amount [NPR]', width: 20, dataIndex: 'credit' },
                    {
                      key: 'Short Narration',
                      width: 30,
                      dataIndex: 'remarks',
                    },
                  ]?.map((column) => (
                    <div
                      key={column.key}
                      style={{
                        display: 'table-cell',
                        width: `${Number(column?.width || 0)}%`,
                        padding: '7px',
                        borderRight: `1px solid ${borderColor}`,
                        borderBottom: `1px solid ${borderColor}`,
                        borderLeft: `1px solid ${borderColor}`,
                        boxSizing: 'border-box',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        fontWeight: 700,

                        textAlign: [
                          'S.N.',
                          'Account Name',
                          'Short Narration',
                        ].includes(column?.key)
                          ? 'left'
                          : 'right',
                      }}
                    >
                      {column?.key}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {(type === 'receipt' || type === 'payment') && (
            <>
              <div
                style={{
                  display: 'table',
                  width: '100%',
                  borderCollapse: 'collapse',
                }}
              >
                {voucherData
                  .filter((item) =>
                    type === 'payment'
                      ? Number(item?.creditAmount || 0) > 0
                      : Number(item?.debitAmount || 0) > 0,
                  )
                  .map((row, rowIndex) => (
                    <div key={rowIndex} style={{ display: 'table-row' }}>
                      {[
                        {
                          key: 'Account Name',
                          width: 50,
                          dataIndex: 'ledgerName',
                        },
                        {
                          key: 'Amount [NPR]',
                          width: 20,
                          dataIndex:
                            type === 'payment' ? 'creditAmount' : 'debitAmount',
                        },
                        {
                          key: 'Short Narration',
                          width: 30,
                          dataIndex: 'remarks',
                        },
                      ].map((column) => (
                        <div
                          key={column.key}
                          style={{
                            display: 'table-cell',
                            width: `${column.width}%`,
                            padding: '7px',
                            borderRight: `1px solid ${borderColor}`,
                            borderBottom: `1px solid ${borderColor}`,
                            borderLeft: `1px solid ${borderColor}`,
                            whiteSpace: 'wrap',
                            textAlign: [
                              'S.N.',
                              'Account Name',
                              'Short Narration',
                            ].includes(column.key)
                              ? 'left'
                              : 'right',
                          }}
                        >
                          {row?.[column?.dataIndex]}
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
        <div
          style={{
            textAlign: 'start',
            padding: '20px 0',
            fontWeight: 500,
            fontSize: 14,
            display: 'flex',
          }}
        >
          <div
            style={{
              paddingRight: '8px',
            }}
          >
            Long Narration :
          </div>
          <div
            style={{
              width: '60%',
              borderBottom: `1px solid ${borderColor}`,
            }}
          >
            {data?.narration || ''}
          </div>
        </div>

        <div
          style={{
            textAlign: 'start',
            padding: '20px 0',
            fontWeight: 500,
            fontSize: 14,
            display: 'flex',
          }}
        >
          <div
            style={{
              paddingRight: '8px',
            }}
          >
            Amount In Words :
          </div>
          <div
            style={{
              width: '60%',
              borderBottom: `1px solid ${borderColor}`,
            }}
          >
            {currencyType === '$' ? '$ ' : '[NPR]  '}
            {currencyType === '$'
              ? toWordsUSD.convert(Number(totals.debitAmount?.toFixed(3)))
              : toWords.convert(Number((totals.debitAmount ?? 0).toFixed(3)))}
          </div>
        </div>
      </div>
    </>
  );
};
